import { TTextAnalysis } from "../../services/types/sessionSummary.types";
import styles from "./textAnalysisInPopup.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";

interface PropsForText {
  data: TTextAnalysis | null;
}

function TextAnalysisInPopup({ data }: PropsForText) {
  const hasData = data && (
    data.statistics ||
    (data.keywords && data.keywords.length > 0) ||
    (data.main_topics && data.main_topics.length > 0) ||
    (data.insights && data.insights.length > 0) ||
    (data.key_observations && data.key_observations.length > 0 && data.key_observations[0] !== "") ||
    data.sentiment_analysis
  );

  if (!hasData) {
    return (
      <div className={styles.noInformationContainer}>
        <p className={styles.noInformation}>{t("NO_INFORMATION_WAS_FOUND")}</p>
      </div>
    );
  }

  const sentimentData = data.sentiment_analysis;
  const overallSum = sentimentData?.overall
    ? sentimentData.overall.positive + sentimentData.overall.negative
    : 0;
  const positivePercent = overallSum > 0
    ? Math.round((sentimentData!.overall.positive / overallSum) * 100)
    : 50;
  const negativePercent = overallSum > 0
    ? Math.round((sentimentData!.overall.negative / overallSum) * 100)
    : 50;
  const neutralPercent = Math.max(0, 100 - positivePercent - negativePercent);

  return (
    <article className={styles.article}>
      <h3 className={styles.mainTitle}>📊 {t("TEXT_ANALYSIS_ADVANCED") || "ניתוח טקסט מתקדם"}</h3>

      {data.statistics && (
        <div className={styles.statsGrid}>
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>📝</div>
              <div className={styles.statsLabel}>{t("WORD_COUNT") || "ספירת מילים"}</div>
            </div>
            <div className={styles.statsValue}>{data.statistics.word_count?.toLocaleString() || 0}</div>
            <div className={styles.statsSubtitle}>{t("WORDS_TOTAL") || "מילים סה\"כ"}</div>
          </div>
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>💬</div>
              <div className={styles.statsLabel}>{t("SENTENCES") || "משפטים"}</div>
            </div>
            <div className={styles.statsValue}>{data.statistics.sentence_count?.toLocaleString() || 0}</div>
            <div className={styles.statsSubtitle}>{t("SENTENCES_TOTAL") || "משפטים סה\"כ"}</div>
          </div>
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>📚</div>
              <div className={styles.statsLabel}>{t("MEDICAL_TERMS") || "מונחים רפואיים"}</div>
            </div>
            <div className={styles.statsValue}>{data.statistics.medical_terms_count || 0}</div>
            <div className={styles.statsSubtitle}>{t("UNIQUE_TERMS") || "מונחים ייחודיים"}</div>
          </div>
          <div className={styles.statsCard}>
            <div className={styles.statsCardHeader}>
              <div className={styles.statsIcon}>🎯</div>
              <div className={styles.statsLabel}>{t("READABILITY_LEVEL") || "רמת קריאות"}</div>
            </div>
            <div className={styles.statsValue}>{data.statistics.readability_score || 0}</div>
            <div className={styles.statsSubtitle}>{t("READABILITY_SCORE") || "ציון קריאות"}</div>
          </div>
        </div>
      )}

      {data.keywords && data.keywords.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>🔑 {t("MAIN_KEYWORDS") || "מילות מפתח עיקריות"}</h4>
          <div className={styles.keywordsCloud}>
            {data.keywords.map((keyword, index) => (
              <span key={index} className={styles.keywordTag}>{keyword}</span>
            ))}
          </div>
        </div>
      )}

      {data.main_topics && data.main_topics.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>📌 {t("MAIN_TOPICS") || "נושאים מרכזיים"}</h4>
          <div className={styles.topicsList}>
            {data.main_topics.map((topic, index) => (
              <div key={index} className={styles.topicItem}>
                <div className={styles.topicTitle}>{topic.icon || "📋"} {topic.title}</div>
                <div className={styles.topicDescription}>{topic.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.insights && data.insights.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>💡 {t("COMPUTED_INSIGHTS") || "תובנות ממוחשבות"}</h4>
          <ul className={styles.insightsList}>
            {data.insights.map((insight, index) => (
              <li key={index} className={styles.insightItem}>
                <div className={styles.insightIcon}>{insight.icon || "💡"}</div>
                <div className={styles.insightContent}>
                  <div className={styles.insightTitle}>{insight.title}</div>
                  <div className={styles.insightText}>{insight.text}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.key_observations && data.key_observations.length > 0 && data.key_observations[0] !== "" && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>👁️ {t("KEY_OBSERVATIONS") || "תצפיות מפתח"}</h4>
          <ul className={styles.observationsList}>
            {data.key_observations.map((observation, index) => (
              <li key={index} className={styles.observationItem}>
                <span className={styles.observationIndex}>{index + 1}.</span> {observation}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.clinicalRow}>
        {data.clinical_flags && data.clinical_flags.length > 0 && (
          <div className={styles.clinicalCard}>
            <h4 className={styles.clinicalCardTitle}>⚠️ {t("CLINICAL_FLAGS") || "דגלים קליניים"}</h4>
            <ul className={styles.flagsList}>
              {data.clinical_flags.map((flag, index) => (
                <li key={index} className={styles.flagItem}>{flag}</li>
              ))}
            </ul>
          </div>
        )}
        {data.risk_assessment && (
          <div className={`${styles.clinicalCard} ${styles.riskCard}`}>
            <h4 className={styles.clinicalCardTitle}>🔒 {t("RISK_ASSESSMENT") || "הערכת סיכון"}</h4>
            <div className={styles.riskLevel} data-level={data.risk_assessment.level?.toLowerCase()}>
              {data.risk_assessment.level}
            </div>
            <p className={styles.riskJustification}>{data.risk_assessment.justification}</p>
          </div>
        )}
      </div>

      {data.linguistic_patterns && data.linguistic_patterns.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>🗣️ {t("LINGUISTIC_PATTERNS") || "דפוסים לשוניים"}</h4>
          <ul className={styles.patternsList}>
            {data.linguistic_patterns.map((pattern, index) => (
              <li key={index} className={styles.patternItem}>
                <span className={styles.patternIndex}>{index + 1}.</span> {pattern}
              </li>
            ))}
          </ul>
        </div>
      )}

      {sentimentData && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>😊 {t("TEXTUAL_SENTIMENT_ANALYSIS") || "ניתוח סנטימנט טקסטואלי"}</h4>
          <div className={styles.sentimentIndicator}>
            <div className={styles.sentimentItem}>
              <div className={styles.sentimentEmoji}>😊</div>
              <div className={styles.sentimentLabel}>{t("POSITIVE") || "חיובי"}</div>
              <div className={styles.sentimentValue}>{positivePercent}%</div>
            </div>
            <div className={styles.sentimentItem}>
              <div className={styles.sentimentEmoji}>😐</div>
              <div className={styles.sentimentLabel}>{t("NEUTRAL") || "נייטרלי"}</div>
              <div className={styles.sentimentValue}>{neutralPercent}%</div>
            </div>
            <div className={styles.sentimentItem}>
              <div className={styles.sentimentEmoji}>😟</div>
              <div className={styles.sentimentLabel}>{t("NEGATIVE") || "שלילי"}</div>
              <div className={styles.sentimentValue}>{negativePercent}%</div>
            </div>
          </div>
        </div>
      )}

      {data.scope_note && (
        <div className={styles.scopeNote}>
          <p>* {data.scope_note}</p>
        </div>
      )}
    </article>
  );
}

export default TextAnalysisInPopup;
